import { Box, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useDealContext } from "../../deal/context";
import { ExecTabs, ExecTab } from "../../company/styles/AdvisorStyles";
import { useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ProjectHttpClient from "../../../core/infrastructure/clients/v1/ProjectHttpClient";
import { Navigate, useParams } from "react-router-dom";
import Loading from "../../../components/Loading";
import { IAdvisor, ITouchpoint, IViewpoint, ViewpointStatus } from "../../../types";
import { TouchpointResponse } from "../components/touchpoint/TouchpointResponse";
import { TouchpointAnalytics } from "../components/touchpoint/TouchpointAnalytics";
import { AdvisorTitle } from "../components/AdvisorTitle";
import { useAdvisor } from "../context/AdvisorContext";
import ClientProvider from "../../../modules/ClientProvider";
import { FollowUpPrompts } from "../components/meetings/FollowupPrompts";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";
import { Link } from "react-router-dom";

const TouchpointSurvey = ({ advisor, viewpoint, touchpoint, setSurveyCompleted }: { advisor: IAdvisor, viewpoint: IViewpoint | null, touchpoint: ITouchpoint, setSurveyCompleted: (completed: boolean) => void }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState<string>("600px");

    // Mobile detection function
    const isMobileDevice = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
               (window.innerWidth <= 768);
    };

    useEffect(() => {
        // Check if user is on mobile and redirect if so
        if (isMobileDevice()) {
            const url = viewpoint?.alchemer_survey_url || `${touchpoint.default_survey_url}?blid=${advisor.public_id}&tpid=${touchpoint.uuid}`;
            window.location.href = url;
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            // Listen for height updates from the iframe content
            if (event.data && typeof event.data === 'object' && event.data.type === 'resize') {
                const newHeight = event.data.height + 100;
                if (newHeight && typeof newHeight === 'number') {
                    // Enforce minimum height of 400px to prevent too-small iframes
                    const safeHeight = Math.max(newHeight, 400);
                    setIframeHeight(`${safeHeight}px`);
                    // Remove the duplicate setTimeout that could cause race conditions
                }
            } else if (event.data && typeof event.data === 'object' && event.data.type === 'status') {
                if (event.data.status === 'completed') {
                    setSurveyCompleted(true);
                }
            }
        };

        const resizeIframe = () => {
            const iframe = iframeRef.current;
            if (iframe) {
                try {
                    // Try to access iframe content (works for same-origin)
                    const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
                    if (iframeDocument) {
                        const body = iframeDocument.body;
                        const html = iframeDocument.documentElement;

                        // Measure content height without modifying the iframe DOM
                        const height = Math.max(
                            body?.scrollHeight || 0,
                            body?.offsetHeight || 0,
                            html?.clientHeight || 0,
                            html?.scrollHeight || 0,
                            html?.offsetHeight || 0
                        );

                        if (height > 0) {
                            // Enforce minimum height of 400px and only update if significantly different
                            const safeHeight = Math.max(height, 400);
                            const currentHeight = parseInt(iframeHeight);
                            
                            // Only update if height difference is more than 20px to avoid micro-adjustments
                            if (Math.abs(safeHeight - currentHeight) > 20) {
                                setIframeHeight(`${safeHeight}px`);
                            }
                        }
                    }
                } catch (error) {
                    // Cross-origin iframe - rely on postMessage for height updates
                    console.log('Cross-origin iframe detected, using postMessage for height updates');
                }
            }
        };

        // Listen for postMessage events for cross-origin iframes
        window.addEventListener('message', handleMessage);

        // Set up iframe load event listener
        const iframe = iframeRef.current;
        if (iframe) {
            iframe.addEventListener('load', resizeIframe);

            // Single delayed resize attempt to avoid flicker
            iframe.addEventListener('load', () => {
                setTimeout(resizeIframe, 1000); // Single resize attempt after content loads
            });
        }

        // Less frequent checks, only for first 15 seconds to reduce flicker
        const interval = setInterval(resizeIframe, 3000);
        const stopInterval = setTimeout(() => {
            clearInterval(interval);
        }, 15000); // Stop checking after 15 seconds

        return () => {
            window.removeEventListener('message', handleMessage);
            if (iframe) {
                iframe.removeEventListener('load', resizeIframe);
            }
            clearInterval(interval);
            clearTimeout(stopInterval);
        };
    }, [touchpoint.default_survey_url, iframeHeight, advisor.public_id, touchpoint.uuid, viewpoint?.alchemer_survey_url]);

    if (!touchpoint.default_survey_url || touchpoint.default_survey_url === "") {
        return <Loading />;
    }

    // Don't render anything for mobile (redirect happens in useEffect)
    if (isMobileDevice()) {
        return <Loading />;
    }

    const url = viewpoint?.alchemer_survey_url || `${touchpoint.default_survey_url}?blid=${advisor.public_id}&tpid=${touchpoint.uuid}`;
    return (
        <Box sx={{ width: "100%", minHeight: "100vh" }}>
            <iframe
                ref={iframeRef}
                src={url}
                frameBorder="0"
                width="100%"
                height={iframeHeight}
                allow="microphone *; camera *"
                style={{
                    overflow: "hidden",
                    display: "block",
                    border: "none",
                    minHeight: "400px" // CSS fallback minimum height
                }}
            />
        </Box>
    )
}

export const AdvisorTouchpoint = () => {

    const { dealContext } = useDealContext();
    const { advisor, loading } = useAdvisor();

    const project = dealContext.project;
    const { touchpointUuid, section } = useParams();
    const [value, setValue] = useState(section ?? "analytics");
    const { getAccessTokenSilently } = useAuth0();

    const [touchpoint, setTouchpoint] = useState<ITouchpoint | null>(null);
    const [touchpointLoading, setTouchpointLoading] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [showPromptsDialog, setShowPromptsDialog] = useState(false);

    const fetchTouchpoint = async () => {
        if (!touchpointUuid) {
            return;
        }
        setTouchpointLoading(true);
        const accessToken = await getAccessTokenSilently();
        const touchpointClient = ClientProvider.provideAdvisorTouchpointClient();
        const { data, error } = await touchpointClient.getAdvisorTouchpoint(project.tag, touchpointUuid, accessToken);
        if (data?.touchpoint) {
            setTouchpoint(data.touchpoint);
        }
        setTouchpointLoading(false);
    }

    if (touchpointUuid === "latest") {
        const latestViewpoint = advisor?.viewpoints?.[0];
        if (latestViewpoint?.touchpoint) {
            return <Navigate to={`/advisor/feedback/${latestViewpoint?.touchpoint?.uuid}/${section ?? ""}`} />
        }
    }

    useEffect(() => {
        fetchTouchpoint();
    }, [touchpointUuid, project.tag]);

    const viewpoint = touchpoint?.viewpoints?.[0];

    const handleTabSwitch = (event: any, newValue: any) => {
        setValue(newValue);
    }

    const handleSurveyCompleted = () => {
        setSurveyCompleted(true);
        setShowPromptsDialog(true);
    }

    const handleResetSurvey = async () => {
        await fetchTouchpoint();
        setSurveyCompleted(false);
        setShowPromptsDialog(false);
    }

    if (!advisor || !touchpoint || touchpointLoading) {
        return <Loading />;
    }

    if ((!viewpoint || viewpoint.status === ViewpointStatus.NOT_STARTED || viewpoint.status === ViewpointStatus.IN_PROGRESS) && touchpoint && !surveyCompleted) {
        return <TouchpointSurvey advisor={advisor} viewpoint={viewpoint || null} touchpoint={touchpoint} setSurveyCompleted={handleSurveyCompleted} />;
    }

    if (surveyCompleted) {

        return (
            <Box>
                <AdvisorTitle project={project} section={`Feedback > ${touchpoint?.name}`} subheading="Review your response and what other advisors had to say." />
                <DisplayBoxBase className="gray column center" sx={{ width: "100%", padding: "20px" }}>
                    <h5>Thank you for providing your feedback.</h5>
                    <Link to={`/advisor/feedback/${touchpoint?.uuid}/${section ?? ""}`} onClick={() => handleResetSurvey()}>View your response</Link>
                </DisplayBoxBase>
                <Dialog open={showPromptsDialog} onClose={() => setShowPromptsDialog(false)} maxWidth={false} PaperProps={{
                    sx: {
                        width: '750px',
                        maxWidth: '750px'
                    }
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <h5>Three ways you can help {advisor.survey_link.project.name}</h5>
                                <Typography variant="body1" mb={2}>Thanks for taking the time to provide your feedback. Here are additional ways you can help {advisor.survey_link.project.name}.</Typography>
                                <FollowUpPrompts advisor={advisor} />
                            </Box>
                        </Box>
                    </Box>
                </Dialog>
            </Box>
        )
    }

    return (
        <Box>
            <AdvisorTitle project={project} section={`Feedback > ${touchpoint?.name}`} subheading="Review your response and what other advisors had to say." />

            <Box>
                <ExecTabs variant="fullWidth" value={touchpoint?.advisor_analysis_url ? value : "response"} onChange={handleTabSwitch} sx={{ marginBottom: "0", position: "relative", zIndex: 1000 }}>
                    <ExecTab label={<span>Summary Analytics</span>} disabled={!touchpoint?.advisor_analysis_url} value="analytics"></ExecTab>
                    <ExecTab label={<span>Your Response</span>} value="response"></ExecTab>
                </ExecTabs>
            </Box>
            {value === "analytics" && <TouchpointAnalytics project={project} touchpoint={touchpoint} />}
            {(!touchpoint.advisor_analysis_url || value === "response") && <TouchpointResponse project={project} touchpoint={touchpoint} />}
        </Box>
    )
}